import {FC, useCallback} from 'react';
import {useParams} from 'react-router';
import {useStateLoader, useBreadcrumbs} from '@hooks/panelHooks';
import {IGoalGroup} from '@interfaces/consumer/refs';
import {Form, Formik, FormikHelpers} from 'formik';
import {toastr} from '@/utils/toastHelper';
import {Card, Col, Row, Spinner} from "react-bootstrap";
import {FormPageFooter, FormPageHeader} from "@components/falcon/common/forms";
import {BaseInfoBlock} from "./forms";
import * as Yup from 'yup';
import ConsumerRefService from "@api/consumer/consumerRefService";
import withPage from "@components/hoc/withPage";

const EditGoalGroupPage: FC = () => {

    const {group_id} = useParams<{ group_id: string }>();
    const loadGoalGroup = useCallback(() => {
        const api = new ConsumerRefService();
        return api.getGoalGroup(group_id || '');
    }, [group_id]);
    const {data: goalGroup, loading} = useStateLoader<IGoalGroup>(loadGoalGroup);

    useBreadcrumbs('consumer.goals.groups.current.name', true, goalGroup?.name);

    if (!goalGroup) {
        return (
            <Card>
                <Card.Body className="text-center">
                    {loading ? <Spinner className="mr-2" color="primary"/> : (
                        "Группа не найдена"
                    )}
                </Card.Body>
            </Card>
        )
    }

    return (
        <EditGoalGroupForm goalGroup={goalGroup} />
    )
}

interface EditGoalGroupFormProps {
    goalGroup: IGoalGroup;
}

interface EditGoalGroupFormValues {
    name: string;
    description: string;
}

const EditGoalGroupForm: FC<EditGoalGroupFormProps> = ({goalGroup}) => {

    const initialValues: EditGoalGroupFormValues = {
        name: goalGroup.name,
        description: goalGroup.description || ''
    }

    const validationSchema = Yup.object<EditGoalGroupFormValues>({
        name: Yup.string()
            .max(256)
            .required(),
        description: Yup.string()
            .max(10000)
    });

    const onSubmit = async (values: EditGoalGroupFormValues, actions: FormikHelpers<EditGoalGroupFormValues>) => {
        const api = new ConsumerRefService();
        const result = await api.patchGoalGroup({
            id: goalGroup.id,
            name: values.name,
            description: values.description
        });

        if (result.success) {
            toastr.success("Изменение группы", "Группа достижений успешно обновлена");
        }
        else {
            toastr.error('Изменение группы', 'Неизвестная ошибка');
        }
    }

    return (
        <Formik
            initialValues={initialValues}
            onSubmit={onSubmit}
            validationSchema={validationSchema}
        >
            <Form>
                <Row className="g-3">
                    <Col xs={12}>
                        <FormPageHeader title={`Редактирование группы "${goalGroup.name}"`} />
                    </Col>
                    <Col xs={12}>
                        <BaseInfoBlock />
                    </Col>
                    <Col>
                        <FormPageFooter
                            cancelUrl={'/consumer/goals/groups/'}
                        />
                    </Col>
                </Row>
            </Form>
        </Formik>
    )
}

export default withPage(EditGoalGroupPage);
